import React, { useState, useEffect, useCallback } from 'react';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { axiosInstance } from '../apis/axiosConfig';
import { useSelector } from 'react-redux';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Modal, Form, Input, DatePicker, TimePicker, Button, Popconfirm } from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import ColorSelector from '../components/ColorSelector';

dayjs.locale('en');

const localizer = momentLocalizer(moment);

const Calendar = () => {
  const [events, setEvents] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [form] = Form.useForm();
  const user = useSelector(state => state.auth.user);
  const [previousColors, setPreviousColors] = useState(['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#eb2f96', '#fa541c']);

  useEffect(() => {
    if (user) {
      fetchEvents();
    }
  }, [user]);

  const fetchEvents = async () => {
    try {
      // const response = await axiosInstance.get('http://localhost:4000/api/events');
      const response = await axiosInstance.get(process.env.REACT_APP_API_EXTENDED_NEURO_URL + '/events');
      setEvents(response.data.map(event => ({
        ...event,
        start: new Date(event.start),
        end: new Date(event.end)
      })));
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const handleSelectSlot = ({ start }) => {
    setSelectedEvent(null);
    form.resetFields();
    form.setFieldsValue({ 
      startDate: dayjs(start),
      endDate: dayjs(start),
      startTime: dayjs(start),
      endTime: dayjs(start).add(1, 'hour'),
      color: previousColors[0]
    });
    setIsModalVisible(true);
  };

  const handleSelectEvent = (event) => {
    console.log('Selected event:', event);
    setSelectedEvent(event);
    form.setFieldsValue({
      title: event.title,
      startDate: dayjs(event.start),
      endDate: dayjs(event.end),
      startTime: dayjs(event.start),
      endTime: dayjs(event.end),
      color: event.color
    });
    setIsModalVisible(true);
  };

  const handleModalOk = () => {
    form.validateFields().then(async (values) => {
      const { startDate, endDate, startTime, endTime, ...rest } = values;
      const start = startDate.hour(startTime.hour()).minute(startTime.minute()).second(0);
      const end = endDate.hour(endTime.hour()).minute(endTime.minute()).second(0);

      const eventData = {
        ...rest,
        start: start.toDate(),
        end: end.toDate(),
        color: values.color || '#3174ad'
      };

      console.log('Event data to be saved:', eventData);
      console.log('Selected event:', selectedEvent);

      try {
        if (selectedEvent) {
          console.log('Updating event with ID:', selectedEvent._id);
          const response = await axiosInstance.post(`/events/${selectedEvent._id}`, eventData);
          console.log('Update response:', response.data);
        } else {
          console.log('Creating new event');
          const response = await axiosInstance.post('/events', eventData);
          console.log('Create response:', response.data);
        }
        await fetchEvents();
        setIsModalVisible(false);
        form.resetFields();
        setSelectedEvent(null);
        if (eventData.color && !previousColors.includes(eventData.color)) {
          setPreviousColors(prevColors => [eventData.color, ...prevColors.slice(0, 7)]);
        }
      } catch (error) {
        console.error('Error saving event:', error);
        console.error('Error details:', error.response?.data);
      }
    });
  };

  const handleDeleteEvent = async () => {
    if (selectedEvent) {
      try {
        // await axiosInstance.delete(`http://localhost:4000/api/events/${selectedEvent._id}`);
        await axiosInstance.delete(process.env.REACT_APP_API_EXTENDED_NEURO_URL + `/events/${selectedEvent._id}`);
        fetchEvents();
        setIsModalVisible(false);
        setSelectedEvent(null);
      } catch (error) {
        console.error('Error deleting event:', error);
      }
    }
  };

  const eventStyleGetter = (event) => {
    const style = {
      backgroundColor: event.color || '#3174ad',
      borderRadius: '3px',
      opacity: 0.8,
      color: 'white',
      border: '0px',
      display: 'flex',
      alignItems: 'center',
      padding: '2px 4px',
      fontSize: '0.8em',
      height: '24px',
      overflow: 'hidden',
    };
    return { style };
  };

  const formats = {
    eventTimeRangeFormat: ({ start, end }, culture, localizer) =>
      localizer.format(start, 'HH:mm', culture) + '-' + localizer.format(end, 'HH:mm', culture),
  };

  const EventComponent = ({ event }) => (
    <div className="event-content">
      <span className="event-time">{moment(event.start).format('HH:mm')}-{moment(event.end).format('HH:mm')}</span>
      <span className="event-title">{event.title}</span>
    </div>
  );

  const dayPropGetter = useCallback((date) => {
    return {
      className: 'custom-day-cell',
    };
  }, []);

  return (
    <div className="h-screen p-8">
      <h1 className="text-3xl font-bold mb-4">Calendar</h1>
      <style jsx global>{`
        .rbc-event {
          min-height: 24px !important;
          height: 24px !important;
        }
        .rbc-events-container {
          margin-right: 0 !important;
        }
        .event-content {
          display: flex;
          align-items: center;
          width: 100%;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }
        .event-time {
          flex-shrink: 0;
          margin-right: 4px;
        }
        .event-title {
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .rbc-row-segment {
          padding: 0 1px 1px 1px !important;
        }
        .rbc-event-content {
          font-size: 0.8em !important;
        }
        .custom-day-cell {
          max-height: 150px;
          overflow-y: auto;
        }
        .custom-day-cell::-webkit-scrollbar {
          width: 4px;
        }
        .custom-day-cell::-webkit-scrollbar-thumb {
          background-color: rgba(0, 0, 0, 0.2);
          border-radius: 2px;
        }
        .rbc-month-view .rbc-month-row {
          overflow: visible !important;
        }
        .rbc-month-view .rbc-row-content {
          z-index: 1;
        }
        .custom-time-picker {
          border: 1px solid #d9d9d9;
          border-radius: 8px;
          padding: 8px;
          font-size: 1em;
          transition: border-color 0.3s ease, box-shadow 0.3s ease;
        }
        .custom-time-picker:hover {
          border-color: #40a9ff;
        }
        .custom-time-picker:focus {
          border-color: #40a9ff;
          box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.2);
          outline: none;
        }
        .ant-picker-time-panel-column {
          width: 50px;
        }
        .ant-picker-time-panel-cell-inner {
          padding: 4px 8px;
          border-radius: 4px;
          transition: background-color 0.3s ease;
        }
        .ant-picker-time-panel-cell-inner:hover {
          background-color: #e6f7ff;
        }
        .ant-picker-time-panel-cell-selected .ant-picker-time-panel-cell-inner {
          background-color: #bae7ff;
          color: #0050b3;
        }
      `}</style>
      <BigCalendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        style={{ height: 'calc(100% - 80px)' }}
        onSelectSlot={handleSelectSlot}
        onSelectEvent={handleSelectEvent}
        selectable
        eventPropGetter={eventStyleGetter}
        dayPropGetter={dayPropGetter}
        formats={formats}
        components={{
          event: EventComponent
        }}
      />
      <Modal
        title={selectedEvent ? "Edit Event" : "Create Event"}
        visible={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedEvent(null);
        }}
        footer={[
          selectedEvent && (
            <Popconfirm
              key="delete"
              title="Are you sure you want to delete this event?"
              onConfirm={handleDeleteEvent}
              okText="Yes"
              cancelText="No"
            >
              <Button danger>Delete</Button>
            </Popconfirm>
          ),
          <Button key="cancel" onClick={() => {
            setIsModalVisible(false);
            setSelectedEvent(null);
          }}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={handleModalOk}>
            {selectedEvent ? "Update" : "Create"}
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical" className="space-y-2">
          <Form.Item name="title" label="Title" rules={[{ required: true }]} className="mb-2">
            <Input />
          </Form.Item>
          <div className="flex space-x-2">
            <Form.Item name="startDate" label="Start Date" rules={[{ required: true }]} className="flex-1 mb-2">
              <DatePicker className="w-full" />
            </Form.Item>
            <Form.Item name="endDate" label="End Date" rules={[{ required: true }]} className="flex-1 mb-2">
              <DatePicker className="w-full" />
            </Form.Item>
          </div>
          <div className="flex space-x-2">
            <Form.Item name="startTime" label="Start Time" rules={[{ required: true }]} className="flex-1 mb-2">
              <TimePicker format="h:mm a" use12Hours className="w-full custom-time-picker" />
            </Form.Item>
            <Form.Item name="endTime" label="End Time" rules={[{ required: true }]} className="flex-1 mb-2">
              <TimePicker format="h:mm a" use12Hours className="w-full custom-time-picker" />
            </Form.Item>
          </div>
          <Form.Item name="color" label="Color" className="mb-2">
            <ColorSelector
              value={form.getFieldValue('color')}
              onChange={(color) => form.setFieldsValue({ color })}
              previousColors={previousColors}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Calendar;
